import { CentreDistrictResDTO } from '@/api/DTO/area/area.interface';
import { Option } from '@/api/DTO/common.interface';
import { OrgUserOutCentre } from '@/api/DTO/user/user.interface';
import { useOrgOutCentreMutation } from '@/api/user/userApiSlice';
import { yupResolver } from '@hookform/resolvers/yup';
import Close from '@mui/icons-material/Close';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Typography,
} from '@mui/material';
import { closeSnackbar, useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import AppDropDownMenu from '../form-inputs/AppDropDownMenu';
import AppRadioGroup from '../form-inputs/AppRadioGroup';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { Centre } from '@/api/enum/centre.enum';

interface WithdrawalMembershipModalProps {
	userId: string;
	disabled?: boolean;
	handleRefetchIsApply?: any;
	outToDistrictsList: CentreDistrictResDTO[];
}

const WithdrawalMembershipModal = (props: WithdrawalMembershipModalProps) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const { enqueueSnackbar } = useSnackbar();
	const navigate = useNavigate();

	const { userId, disabled, outToDistrictsList, handleRefetchIsApply } = props;

	const [orgOutCentre] = useOrgOutCentreMutation();

	const [districtOptions, setDistrictOptions] = useState<Option[]>([]);

	const defaultValues: DefaultValues<OrgUserOutCentre> = {
		centre: outToDistrictsList[0]?.centreEnum ?? '',
	};

	const formSchema = Yup.object().shape({
		centre: Yup.string().required(t('errorMessage.pleaseSelect')),
		districtId: Yup.string().required(t('errorMessage.pleaseSelect')),
	});

	const centreOptions = outToDistrictsList.map((centre) => ({
		id: centre.centreEnum ?? '',
		name: centre.centreName ?? '',
	}));

	const methods = useForm<OrgUserOutCentre>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const watchCentre = methods.watch('centre');

	useEffect(() => {
		methods.setValue('userId', userId);
	}, [userId]);

	useEffect(() => {
		if (watchCentre) {
			setDistrictOptions(
				outToDistrictsList.find((centre) => centre.centreEnum === watchCentre)
					?.districts ?? []
			);
		}
	}, [outToDistrictsList, watchCentre]);

	useEffect(() => {
		if (watchCentre) {
			methods.setValue('districtId', undefined);
		}
	}, [watchCentre]);

	const handleClose = () => {
		setOpen(false);
	};

	const onSubmit: SubmitHandler<OrgUserOutCentre> = async (data) => {
		const response = await orgOutCentre(data);
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.outCentreSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
			if (data.centre == Centre.SHARE) {
				navigate(`/users/non-member-user/${userId}/info`);
			} else {
				handleRefetchIsApply && handleRefetchIsApply();
			}
		}
	};

	return (
		<>
			<MenuItem
				onClick={() => {
					setOpen(true);
				}}
				disabled={disabled}
			>
				<ListItemIcon>
					<PersonOffOutlinedIcon fontSize='small' />
				</ListItemIcon>
				<ListItemText>{t('button.withdrawMembership')}</ListItemText>
			</MenuItem>
			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='xs'
				fullWidth
			>
				<DialogTitle variant='h5'>
					{t('common.withdrawMembershipUser')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<Box display='flex'>
						<ErrorOutlineOutlinedIcon
							fontSize='small'
							sx={{ marginRight: '10px' }}
						/>
						<Trans
							i18nKey={'user.withdrawMembershipMsg'}
							components={{
								Highlight: (
									<Box
										display='inline-block'
										fontWeight={700}
										sx={{
											textDecoration: 'underline',
										}}
									/>
								),
							}}
							variant='body1'
							sx={{ whiteSpace: 'pre-line' }}
						/>
					</Box>

					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onSubmit)}>
							<Grid container spacing={2} marginTop={0} marginBottom={2}>
								<Grid item xs={12}>
									<Typography variant='body2'>
										{t('common.transferInstitution')}
									</Typography>
									<AppRadioGroup
										control={methods.control}
										name='centre'
										row
										options={centreOptions ?? []}
									/>
								</Grid>
								<Grid item xs={12}>
									<AppDropDownMenu
										control={methods.control}
										name='districtId'
										label={t('common.selectLocation')}
										options={districtOptions ?? []}
									/>
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onSubmit)}
					>
						{t('button.confirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default WithdrawalMembershipModal;
